// The SerenityConsult look for a mined process: the main path top to bottom with the median time between steps, the
// rework loops as amber boxes on the side, and the variants as bars. Everything comes from the mined data
// (ProcessView). "Future state" invents nothing: it is the same work with the rework loops taken out, so the numbers
// become what the cases that ran straight through show.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { dur, type PEdge, type ProcessView, type PVariant } from "./pmApi";

const SEP = "\u0001";
const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const list = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
const fit = (s: string, chars: number) => (s.length > chars ? `${s.slice(0, Math.max(1, chars - 1))}…` : s);
const isStraight = (steps: string[]) => new Set(steps).size === steps.length;
function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b), i = (s.length - 1) / 2, lo = Math.floor(i), hi = Math.ceil(i);
  return s[lo] + (s[hi] - s[lo]) * (i - lo);
}

// ---------------------------------------------------------------- Motion

/** True when the person asked the system for less motion. */
export function useReducedMotion(): boolean {
  const q = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
  const [still, setStill] = useState(() => !!q?.matches);
  useEffect(() => {
    if (!q) return;
    const f = () => setStill(q.matches);
    q.addEventListener?.("change", f);
    return () => q.removeEventListener?.("change", f);
  }, [q]);
  return still;
}

/** A number that moves to its new value over `ms` (eased), or jumps there when motion is reduced. */
export function useTween(target: number | null, still: boolean, ms = 650): number | null {
  const [v, setV] = useState(target);
  const cur = useRef(target);
  useEffect(() => {
    const from = cur.current;
    if (target === null || from === null || still || from === target) { cur.current = target; setV(target); return; }
    let raf = 0, t0: number | null = null;
    const step = (t: number) => {
      if (t0 === null) t0 = t;
      const k = 1 - Math.pow(1 - Math.min(1, (t - t0) / ms), 3);
      const x = from + (target - from) * k;
      cur.current = x; setV(x);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, still, ms]);
  return v;
}

function Tw({ v, fmt, still }: { v: number | null; fmt: (x: number) => string; still: boolean }) {
  const x = useTween(v, still);
  return <>{x === null ? "–" : fmt(x)}</>;
}

/** The width of an element, kept up to date, so an SVG can be laid out at its real size (text stays readable). */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(Math.round(el.getBoundingClientRect().width));
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

// ---------------------------------------------------------------- What the data says

/** The main path: the most common variant that finished without repeating a step (else the most common one without repeats). */
export function mainVariant(p: ProcessView): PVariant | null {
  return p.variants.find((v) => isStraight(v.steps) && v.done > 0) ?? p.variants.find((v) => isStraight(v.steps)) ?? null;
}

export type Loop = { from: string; to: string; cases: number; times: number };

const backSet = (edges: PEdge[]) => new Set(edges.filter((e) => e.back).map((e) => e.from + SEP + e.to));

/** Each place a variant goes back and does a step again: the move into the repeated step. */
function loopMoves(steps: string[], back: Set<string>): string[] {
  const seen = new Set<string>(), out: string[] = [];
  let first = "";
  for (let k = 0; k < steps.length; k++) {
    const s = steps[k];
    if (k > 0 && seen.has(s)) {
      const key = steps[k - 1] + SEP + s;
      if (!first) first = key;
      if (steps[k - 1] === s || back.has(key)) out.push(key);
    }
    seen.add(s);
  }
  return out.length ? out : first ? [first] : [];
}

/** Rework loops: where cases go back to a step they already did, with how many cases and how many times in all. */
export function loopsOf(p: ProcessView): Loop[] {
  const back = backSet(p.edges);
  const m = new Map<string, Loop>();
  for (const v of p.variants) {
    if (isStraight(v.steps)) continue;
    const moves = loopMoves(v.steps, back);
    for (const key of new Set(moves)) {
      const [from, to] = key.split(SEP);
      const l = m.get(key) ?? { from, to, cases: 0, times: 0 };
      l.cases += v.count; l.times += moves.filter((x) => x === key).length * v.count;
      m.set(key, l);
    }
  }
  return [...m.values()].sort((a, b) => b.cases - a.cases);
}

// ---------------------------------------------------------------- Process map

const BOX = 34, SIDE = 42, PITCH = 68, TOP = 14;

export function MiningMap({ p }: { p: ProcessView }) {
  const still = useReducedMotion();
  const [future, setFuture] = useState(false);
  const [ref, width] = useWidth<HTMLDivElement>();
  const k = p.kpis, P = p.process.plural, N = p.process.noun;

  const m = useMemo(() => {
    const main = mainVariant(p);
    const steps = main ? main.steps : [...new Set(p.variants[0]?.steps ?? [])];
    const onMain = new Set(steps);
    const loops = loopsOf(p);
    const drawn = loops.filter((l) => onMain.has(l.from) && onMain.has(l.to)).slice(0, 4);
    const isPrefix = (s: string[]) => s.length <= steps.length && s.every((x, i) => steps[i] === x);
    const others = p.variants.filter((v) => isStraight(v.steps) && !isPrefix(v.steps));
    const otherStep = others.flatMap((v) => v.steps).find((s) => !onMain.has(s)) ?? null;
    const straight = p.cases.filter((c) => c.done && !c.rework);
    return {
      main, steps, loops, drawn, rest: loops.length - drawn.length,
      other: others.reduce((s, v) => s + v.count, 0), otherStep,
      straight: straight.length, straightMedian: median(straight.map((c) => c.durationMs)),
      edge: (a: string, b: string) => p.edges.find((e) => e.from === a && e.to === b),
    };
  }, [p]);
  const hasRework = m.loops.length > 0;
  const fut = future && hasRework;

  // Layout, in real pixels for the width the window has.
  const L = useMemo(() => {
    const W = Math.max(300, width || 520);
    const n = Math.max(1, m.steps.length);
    const side = m.drawn.length > 0;
    const mainW = clamp(Math.round(W * 0.42), 136, 210);
    const sideW = side ? clamp(Math.round(W * 0.33), 112, 180) : 0;
    const gap = side ? clamp(Math.round(W * 0.1), 34, 80) : 0;
    const x0 = Math.max(8, Math.round((W - (mainW + gap + sideW)) / 2));
    const cx = x0 + mainW / 2, mx = x0 + mainW, sx = mx + gap;
    const idx = new Map(m.steps.map((s, i) => [s, i]));
    const rowC = (s: string) => TOP + (idx.get(s) ?? 0) * PITCH + BOX / 2;

    // Side boxes: between the two steps a loop joins (or beside the repeated step), pushed down so none overlap.
    const boxes = m.drawn.map((l) => ({ l, c: l.from === l.to ? rowC(l.from) : (rowC(l.from) + rowC(l.to)) / 2 })).sort((a, b) => a.c - b.c);
    let prev = -Infinity;
    for (const b of boxes) { b.c = Math.max(b.c, prev + SIDE + 10); prev = b.c; }

    // Ports on the right edge of a main step, spread out and ordered by where their box sits, so lines don't cross.
    type Port = { key: string; target: number; y: number };
    const ports = new Map<string, Port[]>();
    const add = (step: string, key: string, target: number) => { const l = ports.get(step) ?? []; l.push({ key, target, y: rowC(step) }); ports.set(step, l); };
    for (const b of boxes) {
      const self = b.l.from === b.l.to;
      add(b.l.from, `o${b.l.from}${SEP}${b.l.to}`, b.c - (self ? 1 : 0));
      add(b.l.to, `i${b.l.from}${SEP}${b.l.to}`, b.c + (self ? 1 : 0));
    }
    const portY = new Map<string, number>();
    for (const [step, l] of ports) {
      l.sort((a, b) => a.target - b.target);
      l.forEach((pt, i) => portY.set(pt.key, rowC(step) + (l.length === 1 ? 0 : -10 + (20 * i) / (l.length - 1))));
    }
    const curve = (x1: number, y1: number, x2: number, y2: number) => {
      const dx = Math.max(14, Math.abs(x2 - x1) * 0.55), s = x2 >= x1 ? 1 : -1;
      return `M${x1},${y1} C${x1 + s * dx},${y1} ${x2 - s * dx},${y2} ${x2},${y2}`;
    };
    const loops = boxes.map((b) => {
      const key = b.l.from + SEP + b.l.to;
      const ya = portY.get(`o${key}`)!, yb = portY.get(`i${key}`)!;
      const outY = ya > yb ? b.c + 7 : b.c - 7, inY = ya > yb ? b.c - 7 : b.c + 7;
      const out = curve(mx, ya, sx - 3, outY), back = curve(sx, inY, mx + 3, yb);
      // The count is a badge on the box's top-left corner, clear of the arrows at any width.
      const bw = 12 + 6.6 * String(b.l.cases).length;
      return { ...b, key, out, back, flow: `${out} ${back.replace(/^M/, "L")}`, bx: sx - 6, by: b.c - SIDE / 2 - 9, bw };
    });
    const mainBottom = TOP + (n - 1) * PITCH + BOX;
    const H = Math.max(mainBottom, ...boxes.map((b) => b.c + SIDE / 2)) + 14;
    return { W, H, n, mainW, sideW, x0, cx, sx, rowC, loops, mainChars: Math.floor((mainW - 16) / 7), sideChars: Math.floor((sideW - 16) / 6.8) };
  }, [width, m]);

  const flowDur = Math.max(4, (L.n - 1) * 2.4);
  const truncated = p.cases.length < k.cases;
  const label = `Process map for ${P}. Main path: ${m.steps.join(", ")}.${m.drawn.map((l) => l.from === l.to ? ` ${l.cases} repeat ${l.from}.` : ` ${l.cases} go back from ${l.from} to ${l.to}.`).join("")}`;

  let note: string;
  if (fut) {
    note = m.straight
      ? `Future state: the ${m.straight.toLocaleString()} ${m.straight === 1 ? N : P} that ran straight through, if the rework loops are taken out. Times between steps stay as measured.`
      : `Future state: no finished ${P} ran straight through yet, so there is no cycle time to show.`;
    if (truncated) note += ` From the ${p.cases.length.toLocaleString()} most recent.`;
  } else {
    note = m.main
      ? `Main path: the most common way ${P} ${m.main.done ? "finish" : "run"} without going back, taken by ${m.main.count.toLocaleString()} of ${k.cases.toLocaleString()}.`
      : `No ${N} ran without going back.`;
    if (m.other) note += ` ${m.other.toLocaleString()} took another path${m.otherStep ? `, for example through ${m.otherStep}` : ""}.`;
    if (m.rest) note += ` ${m.rest} more ${m.rest === 1 ? "loop" : "loops"} at other steps.`;
    if (m.other || m.rest) note += " The full map below shows every path.";
    if (!hasRework) note += ` No rework loops in this process.`;
  }

  const stat = {
    cases: fut ? m.straight : k.cases,
    straight: fut ? (m.straight ? 100 : null) : k.straightPct,
    cycle: fut ? m.straightMedian : k.medianMs,
    rework: fut ? 0 : k.reworkPct,
  };

  return (
    <section className={`win pm${fut ? " future" : ""}`} aria-label="Process map">
      <div className="win-bar">
        <div className="win-title">Process map <span>{p.process.name}</span></div>
        <div className="seg" role="group" aria-label="Process state">
          <button type="button" aria-pressed={!fut} onClick={() => setFuture(false)}>Today</button>
          <button type="button" aria-pressed={fut} disabled={!hasRework} title={hasRework ? "The same work with the rework loops taken out" : "No rework loops in this process"} onClick={() => setFuture(true)}>Future state</button>
        </div>
      </div>
      <div ref={ref} className="ms-canvas">
        <svg viewBox={`0 0 ${L.W} ${L.H}`} role="img" aria-label={label}>
          <defs>
            <marker id="ms-mb" viewBox="0 0 10 10" refX="9" refY="5" markerUnits="userSpaceOnUse" markerWidth="9" markerHeight="9" orient="auto"><path className="ms-mkb" d="M0 0L10 5L0 10z" /></marker>
            <marker id="ms-ma" viewBox="0 0 10 10" refX="9" refY="5" markerUnits="userSpaceOnUse" markerWidth="7" markerHeight="7" orient="auto"><path className="ms-mka" d="M0 0L10 5L0 10z" /></marker>
          </defs>
          {m.steps.slice(1).map((s, i) => {
            const y1 = TOP + i * PITCH + BOX, y2 = TOP + (i + 1) * PITCH;
            return <path key={`m${i}`} className="em" d={`M${L.cx},${y1}V${y2 - 3}`} markerEnd="url(#ms-mb)" />;
          })}
          {L.loops.map((l) => <g key={`r${l.key}`}><path className="er" d={l.out} markerEnd="url(#ms-ma)" /><path className="er" d={l.back} markerEnd="url(#ms-ma)" /></g>)}
          {!still && L.n > 1 && (
            <g aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <circle key={`d${i}`} className="dot" r="4.5"><animateMotion dur={`${flowDur}s`} begin={`${(-i * flowDur) / 3}s`} repeatCount="indefinite" path={`M${L.cx},${L.rowC(m.steps[0])}V${L.rowC(m.steps[L.n - 1])}`} /></circle>
              ))}
              {L.loops.map((l, i) => (
                <circle key={`da${l.key}`} className="dota" r="3.5"><animateMotion dur={`${5 + i * 0.8}s`} repeatCount="indefinite" path={l.flow} /></circle>
              ))}
            </g>
          )}
          {m.steps.slice(1).map((s, i) => {
            const e = m.edge(m.steps[i], s);
            const y = TOP + i * PITCH + BOX + (PITCH - BOX) / 2 + 4;
            return <text key={`t${i}`} className="pl" x={L.cx + 10} y={y}>{e ? dur(e.medianMs) : "–"}<title>{e ? `${e.from} to ${e.to}: median ${dur(e.medianMs)}, ${e.count.toLocaleString()} times` : ""}</title></text>;
          })}
          {m.steps.map((s, i) => {
            const node = p.nodes.find((x) => x.id === s);
            return (
              <g key={`n${s}`} className="pn">
                <title>{`${s}: ${node ? `${node.cases.toLocaleString()} ${node.cases === 1 ? N : P}` : ""}`}</title>
                <rect x={L.x0} y={TOP + i * PITCH} width={L.mainW} height={BOX} rx="9" />
                <text x={L.cx} y={TOP + i * PITCH + 21.5} textAnchor="middle">{fit(s, L.mainChars)}</text>
              </g>
            );
          })}
          {L.loops.map((l) => {
            const self = l.l.from === l.l.to;
            return (
              <g key={`s${l.key}`} className="ps">
                <title>{self ? `${l.l.cases} ${l.l.cases === 1 ? N : P} did ${l.l.from} twice in a row, ${l.l.times} times in all` : `${l.l.cases} ${l.l.cases === 1 ? N : P} went back from ${l.l.from} to ${l.l.to}, ${l.l.times} times in all`}</title>
                <rect x={L.sx} y={l.c - SIDE / 2} width={L.sideW} height={SIDE} rx="9" />
                <text className="k" x={L.sx + L.sideW / 2} y={l.c - 4} textAnchor="middle">{self ? "Repeated" : "Back to"}</text>
                <text x={L.sx + L.sideW / 2} y={l.c + 12} textAnchor="middle">{fit(self ? l.l.from : l.l.to, L.sideChars)}</text>
              </g>
            );
          })}
          {L.loops.map((l) => (
            <g key={`c${l.key}`} className="pb" aria-hidden="true">
              <rect x={l.bx} y={l.by} width={l.bw} height="18" rx="9" />
              <text x={l.bx + l.bw / 2} y={l.by + 12.5} textAnchor="middle"><Tw v={fut ? 0 : l.l.cases} fmt={(x) => String(Math.round(x))} still={still} /></text>
            </g>
          ))}
        </svg>
      </div>
      <p className="note" aria-live="polite">{note}</p>
      <p className="ms-legend">
        <span><i />Main path, median time between steps</span>
        {hasRework && <span><i className="a" />Going back, number of {P}</span>}
        <span className="tag">Last {p.days} days</span>
      </p>
      <dl className="ms-stats">
        <div><dt>{cap(P)} analyzed</dt><dd><Tw v={stat.cases} fmt={(x) => Math.round(x).toLocaleString()} still={still} /></dd><small>{fut ? "ran straight through" : `last ${p.days} days`}</small></div>
        <div><dt>Straight through</dt><dd><Tw v={stat.straight} fmt={(x) => `${Math.round(x)}%`} still={still} /></dd><small>{fut ? "of these" : k.done ? `of finished ${P}` : "none finished yet"}</small></div>
        <div><dt>Median cycle</dt><dd><Tw v={stat.cycle} fmt={(x) => dur(x)} still={still} /></dd><small>start to finish</small></div>
        <div><dt>Rework</dt><dd><Tw v={stat.rework} fmt={(x) => `${Math.round(x)}%`} still={still} /></dd><small>{fut ? "loops taken out" : "repeated a step"}</small></div>
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------- Variants

type Tone = "straight" | "rework" | "open";

function describe(v: PVariant, main: PVariant | null, back: Set<string>): { label: string; tone: Tone } {
  const open = v.done === 0;
  if (main && v.key === main.key) return { label: open ? "Main path, not finished yet" : "Main path", tone: open ? "open" : "straight" };
  if (!isStraight(v.steps)) {
    const moves = loopMoves(v.steps, back);
    const [from, to] = (moves[0] ?? "").split(SEP);
    const what = from === to ? `${from} repeated` : `Back from ${from} to ${to}`;
    return { label: `${what}${moves.length > 1 ? `, ${moves.length} times` : ""}${open ? ", not finished" : ""}`, tone: "rework" };
  }
  if (open) return { label: `Still open at ${v.steps[v.steps.length - 1]}`, tone: "open" };
  const mainSteps = main?.steps ?? [];
  const extra = v.steps.filter((s) => !mainSteps.includes(s));
  if (extra.length) return { label: `Through ${list(extra)}`, tone: "straight" };
  const skipped = mainSteps.filter((s) => !v.steps.includes(s));
  if (skipped.length) return { label: `Skips ${list(skipped)}`, tone: "straight" };
  return { label: "Same steps, another order", tone: "straight" };
}

export function MiningVariants({ p, onAll }: { p: ProcessView; onAll?: () => void }) {
  const total = p.kpis.cases, P = p.process.plural, N = p.process.noun;
  const main = mainVariant(p);
  const back = backSet(p.edges);
  const top = p.variants.slice(0, 5);
  const rest = Math.max(0, total - top.reduce((s, v) => s + v.count, 0));
  const pct = (n: number) => (total ? (n / total) * 100 : 0);
  const show = (x: number) => (x > 0 && x < 1 ? "<1%" : `${Math.round(x)}%`);
  const restPaths = Math.max(0, p.kpis.variants - top.length);
  return (
    <section className="win" aria-label="Process variants">
      <div className="win-bar">
        <div className="win-title">Process variants <span>{p.kpis.variants.toLocaleString()} {p.kpis.variants === 1 ? "path" : "paths"}</span></div>
        <span className="tag">{total.toLocaleString()} {total === 1 ? N : P}</span>
      </div>
      <div className="win-body variants">
        {top.map((v) => {
          const d = describe(v, main, back), x = pct(v.count);
          return (
            <div key={v.key} className={`variant ${d.tone === "straight" ? "straight" : d.tone === "open" ? "open" : ""}`}>
              <span>{d.label}</span><b>{show(x)}</b>
              <div className="track" aria-hidden="true"><i style={{ width: `${Math.max(x, 0.8)}%` }} /></div>
              <small className="vsteps">{v.steps.join(" → ")} · {v.count.toLocaleString()} {v.count === 1 ? N : P}{v.done && v.medianMs !== null ? ` · median ${dur(v.medianMs)}` : ""}</small>
            </div>
          );
        })}
        {rest > 0 && (
          <div className="variant open">
            <span>Everything else</span><b>{show(pct(rest))}</b>
            <div className="track" aria-hidden="true"><i style={{ width: `${Math.max(pct(rest), 0.8)}%` }} /></div>
            <small className="vsteps">{restPaths.toLocaleString()} other {restPaths === 1 ? "path" : "paths"} · {rest.toLocaleString()} {rest === 1 ? N : P}</small>
          </div>
        )}
      </div>
      <div className="win-foot ms-key">
        <span><i className="g" />Finished without going back</span>
        <span><i className="a" />Went back to a step</span>
        <span><i className="o" />Not finished yet</span>
        {onAll && <button type="button" className="mini" onClick={onAll}>All paths</button>}
      </div>
    </section>
  );
}
